import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { Space } from '@client/components/_ui/layout/Space';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { selectUser, useAuthStore } from '@client/stores/AuthStore';
import type { RouterInputs, RouterOutputs } from '@client/trpc/client';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { hasPermission } from '@shared/permissions/roles';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { Input, Select, Tag } from 'antd';
import { Building2, UploadIcon } from 'lucide-react';
import { useCallback, useState } from 'react';
import { match } from 'ts-pattern';
import { ClientRowActions, useRouteBackedSearchText } from './client-database-list-helpers';
import { ImportFleetFileModal } from './ImportFleetFileModal';
import { useBackgroundVehicleImportWatcher } from './useBackgroundVehicleImportWatcher';

export const Route = createFileRoute('/console/$orgId/configuration/fleet/client-database')({
    staticData: {
        // The list is a full page so a client record (also full page) replaces it
        // rather than stacking over it; the add drawer still overlays the list.
        presentation: 'fullPage',
        modules: ['fleet'],
        metadata: {
            title: 'Client database',
        },
        ui: {
            icon: Building2,
            title: 'Client database',
            subtitle: 'Consolidators, clients, vehicles and drivers',
            menuDescription: 'Manage the fleet client master database',
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ClientDatabaseList />
        </PageController>
    );
}

type QueryParams = RouterInputs['fleetClientDb']['listFleetClients'];
type ClientRow = NonNullable<RouterOutputs['fleetClientDb']['listFleetClients']>['rows'][number];

const columnHelper = createColumnHelper<ClientRow, NonNullable<QueryParams['orderBy']>>();

function ClientDatabaseList() {
    const orgId = useOrgId();
    const { trpc } = useTrpc();
    const user = useAuthStore(selectUser);
    const isOrgAdmin = hasPermission(user?.roles ?? [], 'org:admin');
    const navigate = useNavigate();

    const [input, setInput] = usePathState<QueryParams>('query-params', {
        limit: DEFAULT_PAGE_SIZE,
        orderBy: { name: 'asc' },
        where: {},
    });

    const { data, error, isPending, refetch } = trpc.fleetClientDb.listFleetClients.useQuery(input);
    const { mutate: deleteClient, isPending: isDeleting } = trpc.fleetClientDb.deleteFleetClient.useMutation();
    const [importKind, setImportKind] = useState<'clients' | 'vehicles' | null>(null);

    // Keeps dismissed still-pending vehicle imports observed (FIFO, error-tolerant)
    // so completion still refreshes the list after the modal unmounts (CUR-540).
    const onBackgroundImportCompleted = useCallback(() => void refetch(), [refetch]);
    const { track: trackDismissedImport } = useBackgroundVehicleImportWatcher(onBackgroundImportCompleted);
    // Controlled filter state, seeded from the (possibly restored) query params so
    // the controls never desync from an active filter on return visits (CUR-451).
    const [searchText, setSearchText] = useRouteBackedSearchText(input.where);
    const typeFilter = typeof input.where?.type === 'string' ? input.where.type : undefined;

    const tableProps = usePrismaTable({
        rowKey: 'id',
        data: data?.rows || [],
        total: data?.total || 0,
        loading: isPending || isDeleting,
        queryParams: input,
        onParamsChange: setInput,
        emptyState: { noRecordsTitle: 'No clients yet' },
        columns: [
            columnHelper.accessor('clientNo', {
                id: 'clientNo',
                header: 'Client no',
                sortable: 'clientNo',
                cell: ({ row }) => row.clientNo,
                width: 110,
            }),
            columnHelper.accessor('name', {
                id: 'name',
                header: 'Name',
                sortable: 'name',
                cell: ({ row }) => row.name,
            }),
            columnHelper.accessor('type', {
                id: 'type',
                header: 'Type',
                sortable: 'type',
                width: 130,
                cell: ({ row }) =>
                    match(row.type)
                        .with('CONSOLIDATOR', () => (
                            <Tag variant="outlined" color="purple">
                                Consolidator
                            </Tag>
                        ))
                        .otherwise(() => <Tag variant="outlined">Client</Tag>),
            }),
            columnHelper.accessor((row) => row.Parent?.name, {
                id: 'parent',
                header: 'Consolidator',
                cell: ({ row }) => row.Parent?.name || '-',
            }),
            columnHelper.accessor('state', {
                id: 'state',
                header: 'State',
                sortable: 'state',
                width: 80,
                cell: ({ row }) => row.state || '-',
            }),
            columnHelper.accessor('vehiclesCount', {
                id: 'vehiclesCount',
                header: 'Vehicles',
                // Not sortable: the card shows the ACTIVE count, which Prisma can't
                // order by (relation-count ordering is unfiltered). See fleetClientOrderBy.
                align: 'center',
                width: 90,
                cell: ({ row }) => String(row.vehiclesCount),
            }),
            columnHelper.accessor('driversCount', {
                id: 'driversCount',
                header: 'Drivers',
                align: 'center',
                width: 90,
                cell: ({ row }) => String(row.driversCount),
            }),
            columnHelper.accessor('status', {
                id: 'status',
                header: 'Status',
                sortable: 'status',
                width: 100,
                cell: ({ row }) => row.status || '-',
            }),
            columnHelper.actions({
                width: 50,
                cell: ({ row }) => (
                    <ClientRowActions
                        orgId={orgId}
                        id={row.id}
                        isOrgAdmin={isOrgAdmin}
                        onDelete={(callback) =>
                            deleteClient(
                                { id: row.id },
                                {
                                    onSuccess() {
                                        callback();
                                        void refetch();
                                    },
                                    onError(error) {
                                        errorMessage.show(error);
                                    },
                                },
                            )
                        }
                    />
                ),
            }),
        ],
    });

    return (
        <>
            <Outlet />

            {importKind && (
                <ImportFleetFileModal
                    kind={importKind}
                    open
                    onClose={() => setImportKind(null)}
                    onSuccess={() => void refetch()}
                    onDismissPending={trackDismissedImport}
                />
            )}

            <TableToolbar
                filters={
                    <Space>
                        <Input.Search
                            placeholder="Search by name or client no"
                            allowClear
                            className="w-72"
                            value={searchText}
                            onChange={(event) => setSearchText(event.target.value)}
                            onSearch={(value) =>
                                setInput((prev) => ({
                                    ...prev,
                                    offset: 0,
                                    where: {
                                        ...prev.where,
                                        ...(value
                                            ? {
                                                  OR: [
                                                      { name: { contains: value, mode: 'insensitive' } },
                                                      { clientNo: { contains: value, mode: 'insensitive' } },
                                                  ],
                                              }
                                            : { OR: undefined }),
                                    },
                                }))
                            }
                        />
                        <Select
                            placeholder="Type"
                            allowClear
                            className="w-40"
                            value={typeFilter}
                            options={[
                                { value: 'CONSOLIDATOR', label: 'Consolidator' },
                                { value: 'CLIENT', label: 'Client' },
                            ]}
                            onChange={(value: 'CONSOLIDATOR' | 'CLIENT' | undefined) =>
                                setInput((prev) => ({
                                    ...prev,
                                    offset: 0,
                                    where: { ...prev.where, type: value },
                                }))
                            }
                        />
                    </Space>
                }
                actions={
                    isOrgAdmin && (
                        <>
                            <Button icon={<UploadIcon />} onClick={() => setImportKind('clients')} type="secondary">
                                Import clients
                            </Button>
                            <Button icon={<UploadIcon />} onClick={() => setImportKind('vehicles')} type="secondary">
                                Import vehicles
                            </Button>
                            <Button.Add.Link
                                label="New client"
                                to="/console/$orgId/configuration/fleet/client-database/add"
                                params={{ orgId }}
                                variant="primary"
                            />
                        </>
                    )
                }
            />

            {error ? (
                <InlineQueryError error={error} onRetry={() => void refetch()} />
            ) : (
                <PrismaTable
                    {...tableProps}
                    onRow={({ id }) => ({
                        className: 'cursor-pointer',
                        onClick(e) {
                            navigate(
                                {
                                    to: '/console/$orgId/configuration/fleet/client-database/details/$id',
                                    params: { orgId, id },
                                },
                                e,
                            );
                        },
                    })}
                />
            )}
        </>
    );
}
